import React, { useState } from 'react';
import { toast } from 'sonner';
import { createClinicianPatient } from '../lib/api';

const STAGES = [
  { value: 'pre', label: 'Pre-menopause' },
  { value: 'peri', label: 'Perimenopause' },
  { value: 'post', label: 'Post-menopause' },
];
const CONCERNS = ['Hot flushes', 'Night sweats', 'Sleep issues', 'Brain fog', 'Anxiety', 'Mood swings', 'Fatigue', 'Weight gain', 'Joint pain', 'Low libido', 'Heavy periods', 'Irregular periods'];

const inputCls = 'w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder-white/30 outline-none focus:border-[var(--aura-purple-light)]';
const labelCls = 'block text-xs text-white/50 mb-1';

export function AddPatientModal({ clinicianId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', date_of_birth: '', menopause_stage: 'peri', phone: '', notes: '' });
  const [concerns, setConcerns] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (c) => setConcerns((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error('Please enter the patient\u2019s name');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address or leave it blank');
      return;
    }
    setSaving(true);
    try {
      const res = await createClinicianPatient(clinicianId, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        date_of_birth: form.date_of_birth || null,
        menopause_stage: form.menopause_stage,
        primary_concerns: concerns,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${res.patient.name} added to your caseload`);
      onCreated?.(res.patient);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create patient. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" data-testid="add-patient-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-dark w-full max-w-lg rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl text-white">Add new patient</h2>
            <p className="text-white/50 text-sm">Create the record first, then upload their lab results from the patient page.</p>
          </div>
          <button type="button" data-testid="add-patient-close" onClick={onClose} className="text-white/50 hover:text-white w-8 h-8 grid place-items-center rounded-full hover:bg-white/10"><i className="fa-solid fa-xmark" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Full name *</label>
            <input data-testid="add-patient-name" value={form.name} onChange={set('name')} placeholder="e.g. Jane Smith" className={inputCls} autoFocus />
          </div>
          <div>
            <label className={labelCls}>Date of birth</label>
            <input data-testid="add-patient-dob" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Stage</label>
            <select data-testid="add-patient-stage" value={form.menopause_stage} onChange={set('menopause_stage')} className={inputCls}>
              {STAGES.map((s) => <option key={s.value} value={s.value} className="text-black">{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Email <span className="text-white/30">(optional — lets the patient log in)</span></label>
            <input data-testid="add-patient-email" type="email" value={form.email} onChange={set('email')} placeholder="patient@example.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone <span className="text-white/30">(optional)</span></label>
            <input data-testid="add-patient-phone" value={form.phone} onChange={set('phone')} placeholder="07…" className={inputCls} />
          </div>
        </div>

        <label className={labelCls}>Primary concerns</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {CONCERNS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`add-patient-concern-${c.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => toggle(c)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-base ${concerns.includes(c) ? 'bg-[rgba(168,85,247,0.3)] border-[rgba(168,85,247,0.6)] text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
            >
              {c}
            </button>
          ))}
        </div>

        <label className={labelCls}>Notes <span className="text-white/30">(optional)</span></label>
        <textarea data-testid="add-patient-notes" value={form.notes} onChange={set('notes')} rows={2} placeholder="Referral reason, context…" className={`${inputCls} resize-none mb-5`} />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-2xl py-3 border border-white/15 text-white/80 hover:bg-white/5 transition-base">Cancel</button>
          <button type="submit" data-testid="add-patient-submit" disabled={saving} className="flex-1 aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-70">
            {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-user-plus" /> Create patient</>}
          </button>
        </div>
      </form>
    </div>
  );
}
